import type { DataOption } from '@/common.types'
import type { FieldSchemaModel } from '@/shared/generated'
import type {
	ErrorSchema,
	JSONSchema,
	JSONSchemaArray,
} from '@/shared/tools/json-schema-utils'
import type { JSONSchemaType } from 'ajv'
import type { ChangeEvent } from 'react'

import React, { useMemo, useRef } from 'react'
import { prop } from 'lodash/fp'
import { InfoIcon, PlusIcon, XIcon } from 'lucide-react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'

import { RHFSelect, RHFTextField } from '@/shared/components/HookForm'
import { Button } from '@/shared/components/ui/button'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/shared/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	getEnumOptions,
	getSchemaDefaultState,
} from '@/shared/tools/json-schema-utils'

type PriceLimitEntity = {
	name: string
	on_violation: string
	type: string
	value: string
}
type PriceLimitRuleProps = {
	rule: PriceLimitEntity
	onRemove(): void
	schema: JSONSchema
	onChange(
		e:
			| ChangeEvent<HTMLInputElement>
			| {
					target: {
						value: string | null | undefined
					}
			  }
	): void
	errorSchema?: ErrorSchema
	columnsOptions: DataOption[]
}
type PriceLimitsFieldsProps = {
	name: string
	fields: { id: string }[] | []
	schema: JSONSchemaArray<PriceLimitEntity>
	columns?: FieldSchemaModel[]
	onAdd(data: PriceLimitEntity): void
	onRemove(index: number): void
}
type PriceLimitsFieldsRowProps = {
	name: string
	index: number
	options: {
		on_violation: DataOption[]
		type: DataOption[]
		columns: DataOption[]
	}
	onRemove(index: number): void
	schema: JSONSchema
}
const PriceLimitsFieldsRow = ({
	onRemove,
	options,
	name,
	index,
	schema,
}: PriceLimitsFieldsRowProps) => {
	const { t } = useTranslation()
	// Watch the type field to get the value of the type field
	const type = useWatch({ name: `${name}.${index}.type` })

	return (
		<TableRow data-testid={`pc-repricing-limit-row-${index}`}>
			<TableCell className='border-none p-2'>
				<div className='grid grid-cols-12 gap-2'>
					<div className='col-span-5'>
						<div className='mb-0' data-testid='pc-repricing-limit-name'>
							<RHFSelect
								className='w-full truncate'
								name={`${name}.${index}.name`}
								placeholder={t('general_select', {
									defaultValue: 'Select',
								})}
								options={options.columns || []}
							/>
						</div>
					</div>

					<div
						className='col-span-2 mb-0'
						data-testid='pc-repricing-limit-value'
					>
						<RHFTextField
							className='w-full truncate'
							name={`${name}.${index}.value`}
							type='number'
							autoComplete='off'
							autoCorrect='off'
							addonAfter={type === 'absolute' ? '#' : '%'}
						/>
					</div>

					<div className='col-span-4 flex space-x-2'>
						<div className='mb-0 w-full' data-testid='pc-repricing-limit-type'>
							<RHFSelect
								className='w-full truncate'
								name={`${name}.${index}.type`}
								placeholder={t('general_select', {
									defaultValue: 'Select',
								})}
								options={options.type || []}
							/>
						</div>
						<div className='flex items-start justify-center pt-2'>
							<Tooltip>
								<TooltipTrigger asChild>
									<InfoIcon className='size-4' />
								</TooltipTrigger>
								<TooltipContent side='top'>
									<ReactMarkdown>
										{prop('items.properties.type.description', schema)}
									</ReactMarkdown>
								</TooltipContent>
							</Tooltip>
						</div>
					</div>
				</div>
			</TableCell>
			<TableCell className='border-none p-2 align-top'>
				<div className='ml-auto flex items-start justify-end gap-2'>
					<div className='mb-0' data-testid='pc-repricing-limit-on_violation'>
						<RHFSelect
							className='w-full truncate'
							name={`${name}.${index}.on_violation`}
							placeholder={t('general_select', {
								defaultValue: 'Select',
							})}
							options={options.on_violation || []}
						/>
					</div>
					<Button
						data-testid='remove-repricing-limit-button'
						size='icon'
						variant='ghost'
						onClick={() => onRemove(index)}
					>
						<XIcon />
					</Button>
				</div>
			</TableCell>
		</TableRow>
	)
}
export const PriceLimitsFields = ({
	fields,
	schema,
	columns,
	onAdd,
	onRemove,
	name,
}: PriceLimitsFieldsProps) => {
	const { t } = useTranslation()
	const addButtonRef = useRef<HTMLButtonElement>(null)
	const options = useMemo(
		() => ({
			on_violation: getEnumOptions(
				prop('items.properties.on_violation', schema)
			),
			type: getEnumOptions(prop('items.properties.type', schema)),
			columns:
				columns?.map(({ name, id }: FieldSchemaModel) => ({
					value: id,
					label: t(name, {
						ns: 'fields',
						defaultValue: name,
					}),
				})) || [],
		}),
		[schema, columns, t]
	)

	const handleAddRule = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		const newItem = getSchemaDefaultState<PriceLimitEntity>(
			schema.items as JSONSchemaType<PriceLimitEntity>
		) as PriceLimitEntity
		onAdd(newItem)
	}
	const handleRemoveRule = (index: number) => {
		onRemove(index)

		// Focus the add button after deleting a rule
		addButtonRef.current?.focus()
	}

	return (
		<div className='space-y-2'>
			<div className='space-y-2 border-b border-solid'>
				<Table>
					<TableHeader className='top-auto!'>
						<TableRow>
							<TableHead className='w-7/12 bg-transparent p-2 text-xs'>
								{schema?.title}
							</TableHead>
							<TableHead className='w-5/12 bg-transparent p-2 text-right text-xs'>
								{prop('items.properties.on_violation.title', schema)}
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{fields.length > 0 ? (
							fields.map((rule, index) => (
								<PriceLimitsFieldsRow
									key={rule.id}
									name={name}
									index={index}
									options={options}
									onRemove={handleRemoveRule}
									schema={schema}
								/>
							))
						) : (
							<TableRow>
								<TableCell colSpan={2} className='border-none'>
									<div className='p-5 text-center text-sm text-muted-foreground'>
										{t('general_no_data', {
											defaultValue: 'No data',
										})}
									</div>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<Button
				data-testid='add-repricing-limit-button'
				ref={addButtonRef}
				variant='ghost'
				onClick={handleAddRule}
			>
				<PlusIcon />
				{t('add_interval', {
					defaultValue: 'Add interval',
				})}
			</Button>
		</div>
	)
}
